/**
 * Camera-centred ocean LOD mesh: 2 m cells round the camera, then square rings whose spacing
 * doubles outward, the last one's outer row stretched into opaque fog. Local `position.y` holds
 * each vertex's level; the water vertex shader (scene/shaders.ts) reads it to geomorph.
 *
 * The mesh snaps to the coarsest spacing (`OCEAN_SNAP`), so every level's lattice stays fixed in
 * the world and vertices never swim. Towards its outer edge each level slides its odd vertices
 * onto the next level's lattice (morph 0 → 1 over the Chebyshev distance from the camera) and
 * fades out wavelengths below four of its cells, so where two rings meet both sides compute the
 * same rest points, wave components and heights: no cracks or T-junction gaps.
 */

/** Detail levels; spacing doubles from `OCEAN_SPACING` at level 0. */
export const OCEAN_LEVELS = 5;
/** Level 0 cell size (m): matches `MESH_WAVELENGTH` (four cells) and the wake simulation's 2 m grid. */
export const OCEAN_SPACING = 2;
/** Coarsest level's spacing; the mesh moves in these steps. */
export const OCEAN_SNAP = OCEAN_SPACING * 2 ** (OCEAN_LEVELS - 1);
/** Half-size (m) of the regular rings; beyond it the last row reaches `OCEAN_HORIZON`. */
const OCEAN_REACH = 1600;
/** Half-size (m) of the outer row: past fully opaque distance fog even from a tower or aerial view. */
const OCEAN_HORIZON = 4000;
/** Widest geomorph (in the level's own cells). */
const MORPH_CELLS = 16;

/** Preset knobs (scene/graphics.ts). */
export interface OceanDetail {
  /** Half-size (m) of the 2 m grid round the camera; it morphs out over its outer edge. */
  near: number;
  /** Width of each coarser ring in its own cells. */
  ring: number;
}

export interface OceanMesh {
  /** Local (x, level, z) per vertex. */
  positions: Float32Array;
  indices: Uint32Array;
  /** Per level: (spacing m, morph start m, 1 / morph width, 0) over the Chebyshev distance from the camera. */
  lod: number[];
  /** Outer half-size (m) of each level, finest first. */
  halfSizes: number[];
}

/**
 * Level `L` covers half-sizes (R[L-1], R[L]]. The camera sits within `OCEAN_SNAP / 2` of the mesh
 * centre on each axis, so a level's inner edge is at most R[L-1] + snap/2 from it and its outer
 * edge at least R[L] - snap/2: morphs start after the first and end before the second (1 m margins).
 */
export function oceanMesh(detail: Readonly<OceanDetail>): OceanMesh {
  const half = OCEAN_SNAP / 2;
  const halfSizes: number[] = [];
  const lod: number[] = [];
  for (let level = 0; level < OCEAN_LEVELS; level++) {
    const spacing = OCEAN_SPACING * 2 ** level, inner = halfSizes[level - 1] ?? 0;
    const last = level === OCEAN_LEVELS - 1;
    // Edges sit on the next level's lattice; each ring leaves room for both margins and a morph of eight cells.
    const reach = level === 0 ? Math.max(detail.near, half + 1 + MORPH_CELLS * spacing)
      : Math.max(inner + detail.ring * spacing, inner + OCEAN_SNAP + 2 + 8 * spacing);
    const outer = last ? OCEAN_REACH : Math.ceil(reach / (2 * spacing)) * 2 * spacing;
    if (outer <= inner + OCEAN_SNAP) throw new Error(`Ocean level ${level} ends at ${outer} m, inside its inner ring`);
    halfSizes.push(outer);
    if (last) {
      lod.push(spacing, 1e9, 1, 0);
      continue;
    }
    const end = outer - half - 1;
    const start = Math.max(level === 0 ? 0 : inner + half + 1, end - MORPH_CELLS * spacing);
    lod.push(spacing, start, 1 / (end - start), 0);
  }

  const positions: number[] = [];
  const indices: number[] = [];
  for (let level = 0; level < OCEAN_LEVELS; level++) {
    const spacing = OCEAN_SPACING * 2 ** level, n = halfSizes[level]! / spacing;
    const hole = level === 0 ? -1 : halfSizes[level - 1]! / spacing;
    const stretch = level === OCEAN_LEVELS - 1 ? OCEAN_HORIZON / OCEAN_REACH : 1;
    const side = 2 * n + 1;
    const ids = new Int32Array(side * side).fill(-1);
    const base = positions.length / 3;
    for (let j = -n; j <= n; j++) {
      for (let i = -n; i <= n; i++) {
        const ring = Math.max(Math.abs(i), Math.abs(j));
        if (ring < hole) continue;
        const scale = ring === n ? stretch : 1;
        ids[(j + n) * side + i + n] = positions.length / 3 - base;
        positions.push(i * spacing * scale, level, j * spacing * scale);
      }
    }
    const id = (i: number, j: number): number => base + ids[(j + n) * side + i + n]!;
    for (let j = -n; j < n; j++) {
      for (let i = -n; i < n; i++) {
        if (i >= -hole && i + 1 <= hole && j >= -hole && j + 1 <= hole) continue;
        const a = id(i, j), b = id(i + 1, j), c = id(i, j + 1), d = id(i + 1, j + 1);
        indices.push(a, c, b, b, c, d);
      }
    }
  }
  return { positions: Float32Array.from(positions), indices: Uint32Array.from(indices), lod, halfSizes };
}
